/**
 * Money formatting helpers.
 *
 * The API returns event prices, discount amounts and transaction amounts in
 * dollars (floats, e.g. 19.99 and 74.54) — the backend converts from its
 * integer-cent columns at its own boundary. Subtracting two such floats
 * produces artifacts like `19.99 - 4.00 = 15.989999999999998`, which otherwise
 * leak straight into the UI. Render every amount through `formatPrice` so it
 * lands as a fixed 2-decimal string.
 *
 * Not every endpoint returns dollars. The Stripe-backed reads — `unit_amount`
 * on the plan lists, `taxAmount`/`total`/`refundAmount` on the subscription
 * previews — return integer cents, and those call sites divide by 100 before
 * formatting. Check what the endpoint emits before choosing.
 */

/**
 * Format a dollar amount for display (no `$`). Null-safe. Rounds to 2 decimals
 * — which is what kills the float artifacts (15.9899… → "15.99").
 *
 * Two presentations, same rounding:
 *
 * - Browse (default, `full` omitted) drops the decimals on whole-dollar amounts
 *   ("50.00" → "50", "15.99" stays) — the compact form marketplace/event cards
 *   are designed around.
 * - Checkout (`full: true`) always keeps both decimals ("50.00" → "50.00",
 *   "0.10" → "0.10"). Order summaries show a charge the user is about to pay, so
 *   they need true currency formatting; a trailing zero must never be dropped.
 *
 * Default to `full: true` on any surface showing real money — a charge the
 * user is about to pay, or a record of one they already paid.
 */
export const formatPrice = (amount, full = false) => {
  const fixed = Number(amount || 0).toFixed(2);
  if (full) return fixed;
  return fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
};

/**
 * Whether an event carries a price worth displaying. Pricing is fee-driven, not
 * type-driven: the backend lets any event hold an `eventFee` (e.g. paid
 * `videos_and_presentations`), while free types (articles, free videos/webinars)
 * have a null/0 fee. Gate price UI on this, not on `eventType`.
 */
export const hasPrice = (event) => Number(event?.eventFee) > 0;

/**
 * Discounted event price (dollars) for an event whose `discountInfo` carries an
 * active coupon. Prefers the backend's pre-computed `finalAmount` (derived in
 * exact integer cents); falls back to a client-side subtraction for older
 * responses that predate that field. Never drops below 0.
 */
export const discountedEventPrice = (event) => {
  const coupon = event?.discountInfo?.coupons;
  if (coupon?.finalAmount != null) return Number(coupon.finalAmount);
  const fee = Number(event?.eventFee || 0);
  const discount = event?.discountInfo?.hasDiscount
    ? Number(coupon?.discountAmount || 0)
    : 0;
  return Math.max(0, fee - discount);
};
